import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm, FormProvider } from 'react-hook-form'
import { Link, useSearchParams } from 'react-router-dom'
import Paper from '@mui/material/Paper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Stepper from '@mui/material/Stepper'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import Alert from '@mui/material/Alert'
import BrandHeader from '../components/BrandHeader'
import AuthLayout from '../components/AuthLayout'
import StepCompany from '../components/StepCompany'
import StepAdmin from '../components/StepAdmin'
import SuccessView from '../components/SuccessView'
import type { RegisterFormData } from '../../../shared/utils/types'
import './Register.scss'
import { api } from '../../../shared/services/API'

// Mapeia o ?erro= que TenantController#iniciarRegistoGoogle e
// OAuth2SuccessHandler acrescentam ao redirecionar de volta para cá — é
// sempre navegação de browser, nunca JSON, por isso o erro só pode chegar
// assim (mesmo padrão do ?erro= em Login.tsx).
const GOOGLE_ERROR_KEYS: Record<string, string> = {
    'oauth-signup': 'register.googleSignupError',
    'oauth': 'register.googleError',
}

export default function Register() {
    const { t } = useTranslation(['auth', 'common'])
    const [searchParams, setSearchParams] = useSearchParams()
    const [activeStep, setActiveStep] = useState(0)
    const [done, setDone] = useState(false)
    // Mensagem devolvida por TenantRegistoResponse — já inclui o email do
    // administrador e o prazo real de confirmação; ver SuccessView.
    const [successMessage, setSuccessMessage] = useState<string | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [apiError, setApiError] = useState('')

    const STEPS = [t('register.stepper.company'), t('register.stepper.admin')]

    // O registo via Google não passa pelo Step 2 (admin) desta página — a
    // conta é criada no callback do backend depois do consentimento, por
    // isso aqui só mostramos o mesmo ecrã de sucesso do registo por password.
    const googleSignupPending = searchParams.get('googleSignup') === 'pendente'

    useEffect(() => {
        const erro = searchParams.get('erro')
        if (!erro) return
        setApiError(t(GOOGLE_ERROR_KEYS[erro] ?? GOOGLE_ERROR_KEYS.oauth))
        setSearchParams((params) => {
            params.delete('erro')
            return params
        }, { replace: true })
        // eslint-disable-next-line react-hooks/exhaustive-deps -- só deve correr quando o parâmetro na URL muda
    }, [searchParams])

    const methods = useForm<RegisterFormData>({
        mode: 'onTouched',
        defaultValues: {
            nome: '',
            telefone: '',
            adminNome: '',
            adminEmail: '',
            adminPassword: '',
        },
    })

    const handleSubmit = async () => {
        setApiError('')
        setIsSubmitting(true)
        const data = methods.getValues()

        try {
            const response = await api.post('/api/public/tenants/register', data)
            setSuccessMessage(response.data?.mensagem ?? null)
            setDone(true)
        } catch (err) {
            console.error(err)
            setApiError(t('register.submitError'))
        } finally {
            setIsSubmitting(false)
        }
    }

    const stepTitles = [t('register.step1.title'), t('register.step2.title')]
    const stepSubtitles = [t('register.step1.subtitle'), t('register.step2.subtitle')]

    return (
        <AuthLayout outerClassName="register-page" containerClassName="card-container">
            <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, border: '1px solid', borderColor: 'divider' }}>
                {done || googleSignupPending ? (
                    // Registo por Google: chega aqui via redirect do browser, sem
                    // corpo de resposta — SuccessView mostra o texto genérico.
                    <SuccessView message={googleSignupPending ? null : successMessage} />
                ) : (
                    <FormProvider {...methods}>
                        <BrandHeader />

                        <Typography variant="h4" sx={{ fontSize: '1.05rem', mb: 1.25 }}>
                            {stepTitles[activeStep]}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" style={{ marginBottom: "10px" }}>
                            {stepSubtitles[activeStep]}
                        </Typography>

                        {apiError && (
                            <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                                {apiError}
                            </Alert>
                        )}

                        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                            {STEPS.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>

                        {activeStep === 0 && (
                            <StepCompany onNext={() => setActiveStep(1)} />
                        )}

                        {activeStep === 1 && (
                            <StepAdmin
                                onBack={() => setActiveStep(0)}
                                onSubmit={handleSubmit}
                                isSubmitting={isSubmitting}
                            />
                        )}

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="body2" color="text.secondary" >
                            {t('register.hasAccount')}{' '}
                            <Link
                                to="/login"
                                style={{
                                    color: '#3730A3',
                                    fontWeight: 500,
                                    textDecoration: 'none',
                                }}
                            >
                                {t('register.signIn')}
                            </Link>
                        </Typography>
                    </FormProvider>
                )}
            </Paper>
        </AuthLayout>
    )
}
